/**
 * ═══════════════════════════════════════════════════════
 * Furniture Estimate PDF Generator
 * ═══════════════════════════════════════════════════════
 * 
 * 디자인인사이트 표준 양식 기준 제작가구 전문견적서 PDF 생성
 * - jsPDF + autoTable 기반
 * - 한글 지원 (Base64 임베딩 대신 HTML 렌더링 후 캡처)
 */

import { getCategoryImageUrl, decomposeForManufacturing } from './furniturePricing';
import { renderIsometricSvg, renderIsometricDataUrl } from '@/lib/iso/renderIsometricSvg';
import { getSync } from '@/lib/uploads/drawingStore';
import costTaxonomy from '@/config/cost_taxonomy.json';

interface EstimateItem {
  no: number;
  name: string;
  code: string;
  w: number;
  d: number;
  h: number;
  qty: number;
  unitPrice: number;
  totalPrice: number;
  category: string;
  basis: string;
  surfaceM2: number;
  hasHardware?: boolean;
  hasSocket?: boolean;
  costBreakdown?: any;
  marketComparison?: {
    naraPrice: number;
    naraUrl: string;
    fursysPrice: number;
    livartPrice: number;
    koaPrice: number;
  };
}

interface Estimate {
  projectName: string;
  recipient?: string;
  generatedAt: string;
  totalItems: number;
  totalQty: number;
  subtotal: number;
  vat: number;
  grandTotal: number;
  discount: number;
  finalGrandTotal: number;
  estimateId?: string;
  items: EstimateItem[];
  categoryBreakdown: { category: string; label: string; itemCount: number; totalQty: number; totalAmount: number }[];
  priceGrade: string;
  warnings: string[];
}

const fmt = (n: number) => n.toLocaleString('ko-KR');

/** 금액 → 한글 표기 */
function amountToKorean(amount: number): string {
  const units = ['', '만', '억', '조'];
  const digits = ['', '일', '이', '삼', '사', '오', '육', '칠', '팔', '구'];
  const subUnits = ['', '십', '백', '천'];
  
  if (amount === 0) return '영원정';
  
  const man = Math.floor(amount / 10000);
  const rest = amount % 10000;
  
  let result = '';
  const groups: number[] = [];
  let n = man;
  while (n > 0) {
    groups.push(n % 10000);
    n = Math.floor(n / 10000);
  }
  
  for (let i = groups.length - 1; i >= 0; i--) {
    const g = groups[i];
    if (g === 0) continue;
    let gStr = '';
    for (let j = 3; j >= 0; j--) {
      const d = Math.floor(g / Math.pow(10, j)) % 10;
      if (d === 0) continue;
      gStr += (d === 1 && j > 0 ? '' : digits[d]) + subUnits[j];
    }
    result += gStr + units[i + 1];
  }
  
  return result + '원정';
}

/**
 * 견적서 HTML을 생성하고 새 브라우저 탭에서 인쇄(PDF 저장 가능)
 */
export function generateEstimatePDF(estimate: Estimate, previewImage?: string, options?: { includeSummary?: boolean; includeDetails?: boolean; author?: string; reviewer?: string; checker?: string; approver?: string; contractMode?: 'standalone' | 'included' }): void {
  const includeSummary = options?.includeSummary ?? true;
  const includeDetails = options?.includeDetails ?? true;
  const contractMode = options?.contractMode || (estimate as any).options?.contractMode || 'included'; // Default to included for public procurement

  const gradeLabel = estimate.priceGrade === 'min' ? '최저가' : estimate.priceGrade === 'avg' ? '평균가' : estimate.priceGrade === 'high' ? '고급형' : '최고급형';
  const dateStr = new Date(estimate.generatedAt).toLocaleDateString('ko-KR', { year: 'numeric', month: '2-digit', day: '2-digit' });
  const koreanAmount = amountToKorean(estimate.grandTotal);

  // --- 전체 패킹 집계 ---
  const globalPackings: { name: string; tag: string; count: number }[] = [];
  estimate.items.forEach(item => {
    const calcParts = (item as any).calcParts || [];
    if (!calcParts.length) return;
    const openRatio = (item as any).openRatio || 0;
    
    // Safety check in case calcParts cannot be parsed by decomposeForManufacturing
    try {
      const plan = decomposeForManufacturing(calcParts, item.w, item.d, item.h, openRatio);
      plan.modules.forEach(mod => {
        const isStackedBox = mod.name.indexOf('상하') >= 0;
        let lowerH = mod.height, upperH = 0;
        if (isStackedBox) {
           mod.factorySteps.forEach(function(s: string) {
              const lm = s.match(/하부 BOX.*?×(\d+)\)/);
              if (lm) lowerH = parseInt(lm[1], 10);
              const um = s.match(/상부 BOX.*?×(\d+)\)/);
              if (um) upperH = parseInt(um[1], 10);
           });
           if (upperH === 0) { lowerH = Math.round(mod.height * 0.68); upperH = mod.height - lowerH; }
           
           const nameLower = `W${mod.width} × D${mod.depth} × H${lowerH}`;
           let fl = globalPackings.find(p => p.name === nameLower && p.tag === '하부박스');
           if(fl) fl.count += item.qty; else globalPackings.push({name:nameLower, tag:'하부박스', count:item.qty});

           const nameUpper = `W${mod.width} × D${mod.depth} × H${upperH}`;
           let fu = globalPackings.find(p => p.name === nameUpper && p.tag === '상부박스');
           if(fu) fu.count += item.qty; else globalPackings.push({name:nameUpper, tag:'상부박스', count:item.qty});
        } else {
           const nameFull = `W${mod.width} × D${mod.depth} × H${mod.height}`;
           let ff = globalPackings.find(p => p.name === nameFull && p.tag === '원박스');
           if(ff) ff.count += item.qty; else globalPackings.push({name:nameFull, tag:'원박스', count:item.qty});
        }
      });
    } catch (e) {
      console.error("Packing extraction failure:", e);
    }
  });
  globalPackings.sort((a,b) => b.count - a.count);
  const totalPkgs = globalPackings.reduce((sum, p) => sum + p.count, 0);

  const html = `<!DOCTYPE html>
<html lang="ko">
<head>
<meta charset="UTF-8">
<title>${estimate.projectName} — 제작가구 전문견적서</title>
<style>
  @import url('https://cdn.jsdelivr.net/gh/orioncactus/pretendard/dist/web/static/pretendard-dynamic-subset.css');
  @import url('https://fonts.googleapis.com/css2?family=Noto+Sans+KR:wght@300;400;500;600;700&display=swap');
  
  * { margin: 0; padding: 0; box-sizing: border-box; }
  
  @page { 
    size: A4 portrait; 
    margin: 20mm 15mm;
  }
  
  body {
    font-family: 'Pretendard', -apple-system, 'Noto Sans KR', sans-serif;
    font-size: 9px;
    color: #111;
    background: #e2e8f0;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
    display: flex;
    flex-direction: column;
    align-items: center;
    padding-top: 40px;
    padding-bottom: 40px;
  }
  
  .page { 
    width: 210mm;
    min-height: 297mm;
    background: #fff;
    padding: 20mm 15mm;
    margin-bottom: 24mm;
    box-shadow: 0 12px 24px rgba(0,0,0,0.15);
    box-sizing: border-box;
    flex-shrink: 0;
  }
  
  /* ── 헤더 ── */
  .header {
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    margin-bottom: 20px;
    border-bottom: 3px solid #111;
    padding-bottom: 12px;
  }
  .header-left h1 {
    font-size: 24px;
    font-weight: 800;
    color: #111;
    letter-spacing: -0.5px;
  }
  .header-left .sub {
    font-size: 11px;
    color: #666;
    margin-top: 4px;
  }
  .header-right {
    text-align: right;
    font-size: 10px;
    color: #666;
    line-height: 1.6;
  }
  .header-right .company {
    font-size: 14px;
    font-weight: 800;
    color: #111;
  }
  
  /* ── 결재란 ── */
  .approval-table {
    border-collapse: collapse;
    font-size: 9px;
    color: #111;
    margin-left: auto;
    margin-bottom: 20px;
    table-layout: fixed;
    text-align: center;
  }
  .approval-table td {
    border: 1px solid #111;
    height: 40px;
    vertical-align: bottom;
    padding: 4px;
    width: 60px;
  }
  .approval-table th {
    border: 1px solid #111;
    background: #f1f5f9;
    padding: 4px;
    font-weight: 600;
  }
  
  /* ── 프로젝트 정보 ── */
  .info-bar {
    display: flex;
    gap: 24px;
    background: transparent;
    border-top: 1px solid #111;
    border-bottom: 1px solid #111;
    padding: 10px 0;
    margin-bottom: 16px;
    font-size: 10px;
  }
  .info-bar .item {
    display: flex;
    gap: 8px;
  }
  .info-bar .label { color: #888; font-weight: 600; }
  .info-bar .value { color: #111; font-weight: 700; }
  
  /* ── 금액 박스 ── */
  .amount-box {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    background: #fafafa;
    border-top: 3px solid #111;
    border-bottom: 1px solid #e5e5e5;
    color: #111;
    padding: 16px 20px;
    margin-bottom: 30px;
  }
  .amount-box .korean {
    font-size: 14px;
    font-weight: 600;
    color: #444;
  }
  .amount-box .number {
    font-size: 28px;
    font-weight: 800;
    letter-spacing: -0.5px;
    color: #111;
  }
  .amount-box .number small {
    font-size: 12px;
    font-weight: 500;
    color: #888;
    margin-left: 6px;
  }
  
  /* ── 테이블 ── */
  table {
    width: 100%;
    border-collapse: collapse;
    font-size: 8.5px;
    margin-bottom: 24px;
  }
  thead th {
    background: transparent;
    color: #111;
    padding: 8px 6px;
    text-align: center;
    font-weight: 800;
    font-size: 8.5px;
    border-top: 2px solid #111;
    border-bottom: 1px solid #111;
  }
  tbody td {
    padding: 8px 6px;
    border-bottom: 1px solid #eaeaea;
    text-align: center;
    vertical-align: middle;
    color: #333;
  }
  td.name {
    text-align: left;
    font-weight: 600;
    color: #111;
    max-width: 180px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  td.num { text-align: right; }
  td.price { text-align: right; color: #111; font-weight: 600; }
  td.total-price { text-align: right; color: #111; font-weight: 800; }
  td.cat {
    font-size: 8px;
    color: #666;
  }
  
  /* ── 소계 행 ── */
  .subtotal-row td {
    background: #fafafa;
    font-weight: 800;
    color: #111;
    border-top: 1px solid #111;
    border-bottom: 2px solid #111;
    font-size: 9px;
  }
  
  /* ── 푸터 합계 ── */
  .footer-totals {
    display: flex;
    justify-content: flex-end;
    gap: 0;
    margin-bottom: 24px;
  }
  .footer-totals table {
    width: 320px;
    margin-bottom: 0;
  }
  .footer-totals td {
    padding: 10px 14px;
    font-size: 11px;
    border-bottom: 1px solid #eaeaea;
  }
  .footer-totals .label-cell {
    background: transparent;
    color: #666;
    font-weight: 600;
    text-align: left;
    width: 140px;
  }
  .footer-totals .value-cell {
    text-align: right;
    font-weight: 800;
    color: #111;
    font-size: 12px;
  }
  .footer-totals .grand .label-cell {
    background: transparent;
    color: #111;
    border-top: 2px solid #111;
    border-bottom: 2px solid #111;
  }
  .footer-totals .grand .value-cell {
    color: #111;
    font-size: 15px;
    border-top: 2px solid #111;
    border-bottom: 2px solid #111;
  }
  
  /* ── 카테고리 소계 ── */
  .category-section {
    display: flex;
    gap: 12px;
    flex-wrap: wrap;
    margin-bottom: 16px;
  }
  .cat-card {
    flex: 1;
    min-width: 140px;
    background: #f7fafc;
    border: 1px solid #e2e8f0;
    border-radius: 6px;
    padding: 8px 12px;
  }
  .cat-card .cat-label { font-size: 9px; font-weight: 700; color: #2d3748; margin-bottom: 4px; }
  .cat-card .cat-title { font-size: 10px; font-weight: 600; color: #4a5568; }
  .cat-card .cat-amount { font-size: 11px; font-weight: 700; color: #c53030; margin-top: 4px; }
  
  /* ── 푸터 ── */
  .doc-footer {
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    border-top: 2px solid #1a365d;
    padding-top: 10px;
    font-size: 8px;
    color: #a0aec0;
  }
  .doc-footer .stamp {
    width: 60px;
    height: 24px;
    border: 1px solid #e2e8f0;
    border-radius: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 7px;
    color: #cbd5e0;
  }
  .doc-footer .note {
    font-size: 7.5px;
    color: #a0aec0;
    text-align: right;
  }
  
  /* ── 인쇄 팁 ── */
  .print-tip {
    text-align: center;
    padding: 16px 32px;
    background: #1e293b;
    border-radius: 8px;
    margin-bottom: 32px;
    box-shadow: 0 4px 12px rgba(0,0,0,0.2);
    font-size: 13px;
    color: #f8fafc;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 20px;
  }
  .print-tip strong { color: #fed7aa; }
  
  @media print {
    @page {
      size: A4 portrait;
      margin: 0;
    }
    body {
      background: #fff;
      padding: 0;
      display: block;
    }
    .print-tip { display: none; }
    .page {
      width: 210mm;
      min-height: 297mm;
      height: auto;
      margin: 0 auto;
      padding: 20mm 15mm;
      box-shadow: none;
      break-after: page;
      page-break-after: always;
      box-sizing: border-box;
    }
    .page:last-of-type {
      break-after: avoid;
      page-break-after: avoid;
    }

    table { page-break-inside: auto; }
    tr { page-break-inside: avoid; page-break-after: auto; }
    thead { display: table-header-group; }
    tfoot { display: table-footer-group; }

    [contenteditable="true"] {
      border-bottom: none !important;
    }
  }
</style>
</head>
<body>
<div class="print-tip">
  <span>💡 <strong>Ctrl+P</strong> (또는 <strong>Cmd+P</strong>)를 눌러 <strong>PDF로 저장</strong>하세요 &nbsp;|&nbsp; 용지: <strong>A4 세로</strong> &nbsp;|&nbsp; 여백: <strong>없음(없음)</strong> 으로 설정하세요</span>
  <button onclick="window.print()" style="padding: 8px 20px; background: #3b82f6; color: white; border: none; border-radius: 6px; font-size: 14px; font-weight: 800; cursor: pointer; box-shadow: 0 2px 4px rgba(0,0,0,0.1); transition: background 0.2s;">🖨️ 인쇄 / PDF 저장</button>
</div>

${includeSummary ? `
<div class="page">
  <!-- 헤더 -->
  <div class="header">
    <div class="header-left">
      <div style="display: flex; align-items: center; gap: 16px; margin-bottom: 6px;">
        <img src="https://desinsight.com/images/logo.jpg" alt="DESINSIGHT" style="height: 46px; display: block;" />
        <div style="display: flex; flex-direction: column; justify-content: center;">
          <h1 style="line-height: 1;">제작가구 전문견적서</h1>
          <div class="sub" style="margin-top: 6px;">Custom Furniture Estimate — Generated by SnapGRID</div>
        </div>
      </div>
      
      <div style="margin-top: 20px; font-size: 14px; font-weight: 800; color: #111;">
        <span style="font-size: 11px; font-weight: 600; color: #666; margin-right: 6px;">수신처</span>
        <span contenteditable="true" spellcheck="false" style="border-bottom: 1px dashed #cbd5e1; padding-bottom: 2px; min-width: 140px; display: inline-block; outline: none; transition: border-color 0.2s;" onfocus="this.style.borderBottom='2px solid #3b82f6'" onblur="this.style.borderBottom='1px dashed #cbd5e1'">${estimate.recipient || ''}</span>
        <span style="font-size: 12px; font-weight: 600; margin-left: 4px;">귀하</span>
      </div>
    </div>
    <div class="header-right">
      <div class="company" style="display: flex; align-items: center; justify-content: flex-end; gap: 12px; margin-bottom: 6px;">
        디자인인사이트(주)
        <div style="font-size: 12px; font-weight: 600; color: #111; position: relative;">
          대표자 &nbsp; 배 &nbsp; 경 &nbsp; 철 &nbsp; <span style="font-weight: 400; font-size: 10px;">(인)</span>
          <img src="https://desinsight.com/images/seal.png" alt="인감" style="position: absolute; width: 38px; height: 38px; right: -12px; top: -12px; opacity: 0.9; mix-blend-mode: multiply;" />
        </div>
      </div>
      <div>www.desinsight.com · TEL 02-2603-7252</div>
      <div style="color: #2563eb; font-weight: 600; margin: 2px 0;">견적 문의: ceo@desinsight.com</div>
      <div>경기도 김포시 김포대로 679번길 14, 타운센트 203호</div>
    </div>
  </div>
  
  ${(options?.author || options?.reviewer || options?.approver) ? `
  <table class="approval-table">
    <tr>
      <th rowspan="2" style="width: 20px; background: #e2e8f0; writing-mode: vertical-rl; text-orientation: upright;">결재</th>
      <th>작 성</th>
      <th>검 토</th>
      ${options?.checker ? '<th>확 인</th>' : ''}
      <th>승 인</th>
    </tr>
    <tr>
      <td>${options?.author || ''}</td>
      <td>${options?.reviewer || ''}</td>
      ${options?.checker ? `<td>${options?.checker}</td>` : ''}
      <td>${options?.approver || ''}</td>
    </tr>
  </table>
  ` : ''}
  
  <!-- 프로젝트 정보 -->
  <div class="info-bar">
    <div class="item"><span class="label">발행번호</span><span class="value">${estimate.estimateId || ('EST-' + estimate.generatedAt.slice(0,10).replace(/-/g,'') + '-' + Math.floor(Math.random() * 9000 + 1000))}</span></div>
    <div class="item"><span class="label">현장명</span><span class="value">${estimate.projectName}</span></div>
    <div class="item"><span class="label">견적일</span><span class="value">${dateStr}</span></div>
    <div class="item"><span class="label">품 목</span><span class="value">${estimate.totalItems}종 / ${estimate.totalQty}개</span></div>
    <div class="item"><span class="label">단가기준</span><span class="value">${gradeLabel}</span></div>
  </div>
  
  <!-- 전체 패킹 현황 -->
  <div style="background:#f8fafc; border:1px solid #e2e8f0; border-radius:8px; padding:12px; margin-bottom:16px;">
    <div style="font-size:11px; font-weight:800; color:#0f172a; margin-bottom:8px; display:flex; justify-content:space-between; align-items:flex-end;">
      <div>📦 현장 배송용 총 패킹 규격 리포트 (차량 톤수 산정용)</div>
      <div style="font-size:10px; color:#c2410c; background:#ffedd5; padding:2px 8px; border-radius:12px;">총 ${globalPackings.length}종 · ${totalPkgs}개 패킹 분절</div>
    </div>
    <div style="display:flex; flex-wrap:wrap; gap:6px;">
      ${globalPackings.map(p => `
        <div style="background:#fff; border:1px solid #cbd5e1; padding:4px 8px; border-radius:4px; font-size:9.5px; display:flex; align-items:center; gap:6px;">
          <span style="font-weight:700; color:#1e293b;">${p.name}</span>
          <span style="font-size:8px; padding:1px 4px; border-radius:3px; background:${p.tag==='원박스'?'#f1f5f9':p.tag==='상부박스'?'#dbeafe':'#fce7f3'}; color:${p.tag==='원박스'?'#475569':p.tag==='상부박스'?'#1d4ed8':'#be185d'}; font-weight:700;">${p.tag}</span>
          <span style="color:#b45309; font-weight:900;">${p.count}개</span>
        </div>
      `).join('')}
    </div>
  </div>
  
  <!-- 금액 박스 -->
  <div class="amount-box">
    <div class="korean">일금 ${amountToKorean(estimate.finalGrandTotal || estimate.grandTotal)}</div>
    <div class="number">₩ ${fmt(estimate.finalGrandTotal || estimate.grandTotal)} <small>(VAT 포함)</small></div>
  </div>
  
  <!-- 메인 테이블 -->
  <table>
    <thead>
      <tr>
        <th style="width:25px">NO</th>
        <th style="width:140px">제 품 명</th>
        <th style="width:45px">코드</th>
        <th style="width:40px">W(mm)</th>
        <th style="width:40px">D(mm)</th>
        <th style="width:40px">H(mm)</th>
        <th style="width:35px">겉넓이<br/>㎡</th>
        <th style="width:25px">수량</th>
        <th style="width:60px">단 가</th>
        <th style="width:70px">금 액</th>
        <th style="width:50px">카테고리</th>
        <th style="width:80px">산 출 근 거</th>
      </tr>
    </thead>
    <tbody>
      ${estimate.items.map(item => {
        let panelsStr = '';
        if ((item as any).calcParts && (item as any).calcParts.length > 0) {
            panelsStr = (item as any).calcParts.map((p: any) => `[${p.partName}] ${p.w}×${p.d} (${p.h}T) ${p.qty || 1}매`).join('<br/>');
        } else if (item.category === 'sofa_bench') {
           const hasBackrest = item.h > 500;
           panelsStr = `[하부골조] ${item.w}×${item.d}<br/>[좌판] ${item.w}×${item.d}` + (hasBackrest ? `<br/>[등판] ${item.w}×${item.h-400}` : '') + `<br/>[내장재] 쿠션 스폰지<br/>[표면재] 방염 가죽`;
        } else {
           const T = (item as any).costBreakdown?.thickness || 18;
           const Tb = 9;
           const shelfCount = item.costBreakdown?.shelfCount ?? Math.max(0, Math.floor((item.h - 100) / 350) - 1);
           panelsStr = `[측판] ${item.d}×${item.h-T*2} (${T}T) 2매<br/>[천/바닥판] ${item.w}×${item.d} (${T}T) 2매<br/>[뒷판] ${item.w-T*2}×${item.h-T*2} (${Tb}T) 1매` + (shelfCount > 0 ? `<br/>[선반] ${item.w-T*2}×${item.d-T} (${T}T) ${shelfCount}매` : '');
        }
        return `
      <tr>
        <td>${item.no}</td>
        <td class="name">${item.name}</td>
        <td>${item.code}</td>
        <td class="num">${fmt(item.w)}</td>
        <td class="num">${fmt(item.d)}</td>
        <td class="num">${fmt(item.h)}</td>
        <td class="num">${item.surfaceM2.toFixed(2)}</td>
        <td>${item.qty}</td>
        <td class="price">${fmt(item.unitPrice)}</td>
        <td class="total-price">${fmt(item.totalPrice)}</td>
        <td class="cat">${(item.category || '').replace(/_/g, ' ')}</td>
        <td style="font-size:6.5px;text-align:left;color:#4a5568;line-height:1.3;padding-top:4px;padding-bottom:4px;">
          ${panelsStr}<br/>
          <span style="color:#a0aec0;font-size:6.5px;display:inline-block;margin-top:2px;">[단가기준] ${item.basis || ''}</span>
        </td>
      </tr>`;
      }).join('')}
      <tr class="subtotal-row">
        <td colspan="7"></td>
        <td>${estimate.totalQty}</td>
        <td colspan="1" style="text-align:center">SUB TOTAL</td>
        <td class="total-price">${fmt(estimate.subtotal)}</td>
        <td colspan="2"></td>
      </tr>
    </tbody>
  </table>
  
  <!-- 합계 테이블 -->
  <div class="footer-totals">
    <table>
      <tr>
        <td class="label-cell">공급가액</td>
        <td class="value-cell">₩ ${fmt(estimate.subtotal)}</td>
      </tr>
      <tr>
        <td class="label-cell">부가가치세 (10%)</td>
        <td class="value-cell">₩ ${fmt(estimate.vat)}</td>
      </tr>
      <tr>
        <td class="label-cell">합 계 금 액</td>
        <td class="value-cell">₩ ${fmt(estimate.grandTotal)}</td>
      </tr>
      ${estimate.discount > 0 ? `
      <tr>
        <td class="label-cell" style="color: #ef4444;">단수조정 (에누리)</td>
        <td class="value-cell" style="color: #ef4444;">-₩ ${fmt(estimate.discount)}</td>
      </tr>
      ` : ''}
      <tr class="grand">
        <td class="label-cell">최종 청구 금액</td>
        <td class="value-cell">₩ ${fmt(estimate.finalGrandTotal || estimate.grandTotal)}</td>
      </tr>
    </table>
    
    ${contractMode === 'included' ? `
    <div style="margin-top: 16px; padding: 12px; background: #fee2e2; border-left: 4px solid #ef4444; border-radius: 4px; font-size: 11px; color: #b91c1c; line-height: 1.5; font-weight: 600;">
      ※ [포함공사건 모드] 본 내역서의 일반관리비, 공과잡비, 이윤은 설계 내역 중복 산정을 피하기 위해 내부 품목 및 견적 총괄에서 0 처리되었습니다. 상위 공종(건축공사 전체) 공사원가계산서에서 1회 계상하시기 바랍니다. 상위 공종 미계상 시 업체 수익 결손이 발생합니다.
    </div>
    ` : ''}
  </div>
  
  <!-- 카테고리별 소계 -->
  ${estimate.categoryBreakdown.length > 0 ? `
  <div class="category-section">
    ${estimate.categoryBreakdown.map(cb => {
      const pct = Math.round(cb.totalAmount / estimate.subtotal * 100);
      return `
    <div class="cat-card">
      <div class="cat-label">${cb.label}</div>
      <div class="cat-detail">${cb.itemCount}종 · ${cb.totalQty}개 · ${pct}%</div>
      <div class="cat-amount">₩ ${fmt(cb.totalAmount)}</div>
    </div>`;
    }).join('')}
  </div>` : ''}
  
  ${estimate.warnings.length > 0 ? `
  <div style="background:#fffbeb;border:1px solid #f6e05e;border-radius:6px;padding:8px 12px;margin-bottom:12px;font-size:8px;color:#975a16">
    <strong>⚠ 주의사항</strong><br/>
    ${estimate.warnings.map(w => `• ${w}`).join('<br/>')}
  </div>` : ''}
  
  <!-- 푸터 -->
  <div class="doc-footer">
    <div>
      <div>※ 본 견적서는 SnapGRID Furniture Agent(SnapGRID Furniture Agent)으로 생성되었으며, 확인 후 사용하세요.</div>
      <div>※ 단가 모델: 함양도서관 견적서(93건 · ₩2.249억) 기반 카테고리별 겉넓이(㎡) 단가</div>
      <div style="margin-top:4px">Generated: ${new Date().toISOString().slice(0,19)} · Powered by SnapGRID</div>
    </div>
    <div style="display:flex;gap:8px">
      <div style="text-align:center">
        <div style="font-size:7px;color:#a0aec0;margin-bottom:2px">작성</div>
        <div class="stamp">인</div>
      </div>
      <div style="text-align:center">
        <div style="font-size:7px;color:#a0aec0;margin-bottom:2px">검토</div>
        <div class="stamp">인</div>
      </div>
      <div style="text-align:center">
        <div style="font-size:7px;color:#a0aec0;margin-bottom:2px">승인</div>
        <div class="stamp">인</div>
      </div>
    </div>
  </div>
</div>
` : ''}

  <!-- 아이템별 일위대가 산출서 (Item breakdown sheets) -->
  ${includeDetails ? estimate.items.map((item, idx) => `
  <div class="page" style="${idx > 0 || includeSummary ? 'page-break-before: always;' : ''} margin-top: 12mm;">
    <div class="header" style="border-bottom: 3px solid #111; margin-bottom: 16px; padding-bottom: 12px;">
      <div class="header-left">
        <h2 style="font-size: 16px; color: #111; font-weight: 800;">일위대가 및 상세 산출서</h2>
        <div class="sub" style="font-weight: 600; font-size: 11px; margin-top: 4px;">No.${item.no} — ${item.name} (${item.code})</div>
      </div>
    </div>
    
    <!-- 규격 + 이미지 info-bar -->
    <div style="margin-bottom: 24px; border: 1px solid #cbd5e1; border-radius: 8px; overflow: hidden; display: flex; box-shadow: 0 4px 6px -1px rgba(0,0,0,0.05); align-items: stretch;">
      <div style="flex: 1; padding: 14px 18px; display: flex; flex-direction: column; justify-content: space-around; background: #f8fafc; overflow: hidden;">
        <div style="display: flex; gap: 24px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; margin-bottom: 8px;">
          <div style="display: flex; gap: 8px; align-items: baseline;">
            <div style="font-size: 8.5px; font-weight: 700; color: #64748b; width: 60px;">규격 (W×D×H)</div>
            <div style="font-size: 13px; font-weight: 900; color: #0f172a;">${item.w} <span style="font-size:10px;color:#94a3b8;font-weight:600">×</span> ${item.d} <span style="font-size:10px;color:#94a3b8;font-weight:600">×</span> ${item.h} <span style="font-size:9px;color:#64748b;font-weight:600">mm</span></div>
          </div>
          <div style="display: flex; gap: 8px; align-items: baseline; margin-left: auto;">
            <div style="font-size: 8.5px; font-weight: 700; color: #64748b; width: 60px;">겉넓이 / 체적</div>
            <div style="font-size: 13px; font-weight: 900; color: #0369a1;">${item.surfaceM2.toFixed(3)} <span style="font-size:9.5px;color:#64748b;font-weight:600">㎡</span> <span style="font-size:10px;color:#94a3b8;font-weight:600;margin:0 4px">/</span> ${(item.h * item.w * item.d / 1e9).toFixed(3)} <span style="font-size:9.5px;color:#64748b;font-weight:600">㎥</span></div>
          </div>
        </div>
        <div style="display: flex; gap: 24px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">
          <div style="display: flex; gap: 8px; align-items: center;">
            <div style="font-size: 8.5px; font-weight: 700; color: #64748b; width: 60px;">재질 (마감)</div>
            <div style="font-size: 11.5px; font-weight: 800; color: #1e293b;">${(item.costBreakdown?.materialLabel || 'MDF') + ' ' + ((item as any).thickness ?? 18) + 'T'}</div>
          </div>
          <div style="display: flex; gap: 8px; align-items: center; margin-left: auto;">
            <div style="font-size: 8.5px; font-weight: 700; color: #64748b; width: 60px;">추가 옵션</div>
            <div style="font-size: 10px; font-weight: 700; color: #b45309; display: flex; gap: 6px; align-items: center;">
              ${item.hasHardware ? '<span style="background:#fef3c7;padding:3px 8px;border-radius:4px;">고급 하드웨어</span>' : ''}
              ${item.hasSocket ? '<span style="background:#fef3c7;padding:3px 8px;border-radius:4px;">매립형 콘센트</span>' : ''}
              ${(item as any).hasLighting ? '<span style="background:#fef3c7;padding:3px 8px;border-radius:4px;">간접조명 LED</span>' : ''}
              ${!item.hasHardware && !item.hasSocket && !(item as any).hasLighting ? '<span style="color:#94a3b8">적용 없음</span>' : ''}
            </div>
          </div>
        </div>
      </div>
    </div>
    
    <!-- 일위대가 요약 방정식 블록 -->
    ${(() => {
      const cb = item.costBreakdown;
      if (!cb) return '';
      const mat = cb.materialTotal || 0;
      const labor = cb.manufacturingTotal || 0;
      const exp = item.unitPrice - mat - labor;
      return `<div style="background: #eff6ff; border: 1px solid #bfdbfe; padding: 10px 14px; border-radius: 6px; font-size: 10.5px; font-weight: 800; color: #1e3a8a; display: flex; flex-direction: column; gap: 8px; margin-bottom: 24px;">
        <div style="display: flex; align-items: center; white-space: nowrap;">
          <span style="background:#bfdbfe;color:#1e40af;padding:3px 6px;border-radius:4px;font-size:8px;font-weight:900;margin-right:8px;flex-shrink:0;">단가요약</span>
          자재비 ₩${fmt(mat)} <span style="color:#93c5fd;font-weight:900;margin:0 4px;">+</span> 
          노무비 ₩${fmt(labor)} <span style="color:#93c5fd;font-weight:900;margin:0 4px;">+</span> 
          경비(운송,설치,공과잡비) ₩${fmt(exp)} <span style="color:#cbd5e1;font-weight:900;margin:0 4px;">=</span> 
          <span style="color:#0f172a;">소계 ₩${fmt(item.unitPrice)}</span>
        </div>
        <div style="font-size: 11.5px; display: flex; align-items: center; justify-content: flex-end; padding-top: 6px; border-top: 1px dashed #bfdbfe;">
          <span style="color:#475569;font-size:9.5px;">소계</span> ₩${fmt(item.unitPrice)} 
          <span style="color:#94a3b8;font-size:9px;margin:0 6px;">×</span> 
          <span style="color:#475569;font-size:9.5px;">수량</span> <span style="color:#e11d48;margin-left:3px;">${item.qty}</span>
          <span style="color:#94a3b8;font-size:9px;margin:0 6px;">=</span> 
          <span style="font-size:14px;color:#0f172a;font-weight:900;margin-left:4px;">금액 ₩${fmt(item.totalPrice)}</span>
        </div>
      </div>`;
    })()}

    <!-- 패널 상세 자재 소요량 섹션 -->
    ${(() => {
      const calcParts = (item as any).calcParts || [];
      const rows = calcParts.map((p: any) =>
        '<tr>'
        + '<td style="text-align:left;padding-left:6px;font-weight:700;">' + (p.partName || '') + '</td>'
        + '<td style="text-align:left;padding-left:6px;">' + (p.material || '-') + '</td>'
        + '<td style="text-align:right;padding-right:6px;">' + (p.w || '-') + '</td>'
        + '<td style="text-align:right;padding-right:6px;">' + (p.d || '-') + '</td>'
        + '<td style="text-align:right;padding-right:6px;">' + (p.h || '-') + '</td>'
        + '<td style="text-align:left;padding-left:6px;color:#2c5282;font-weight:600;">' + (p.calculation || '') + '</td>'
        + '</tr>'
      ).join('');
        
      return '<div style="margin-top:12px;margin-bottom:12px;border:1px solid #ddd;font-size:8px;">'
        + '<div style="background:#111;color:#fff;padding:5px 8px;font-size:8.5px;font-weight:800;letter-spacing:0.5px;">▣ 부재별(파츠) 상세 자재 소요량 및 도면 산출근거</div>'
        + '<table style="margin:0;border:none;font-size:7.5px;"><thead><tr style="background:#f5f5f5;">'
        + '<th style="border:1px solid #ddd;width:15%;text-align:center;padding:4px 2px;">파츠/부재명</th>'
        + '<th style="border:1px solid #ddd;width:25%;text-align:center;">재질/마감 (상세)</th>'
        + '<th style="border:1px solid #ddd;width:10%;text-align:center;">W (mm)</th>'
        + '<th style="border:1px solid #ddd;width:10%;text-align:center;">D (mm)</th>'
        + '<th style="border:1px solid #ddd;width:10%;text-align:center;">H (mm/T)</th>'
        + '<th style="border:1px solid #ddd;width:30%;text-align:center;">산출 근거 / 소요량</th>'
        + '</tr></thead><tbody style="border:1px solid #ddd;">'
        + rows
        + '</tbody></table>'
        + '<div style="padding:5px 8px;font-size:7px;color:#555;border-top:1px solid #ddd;background:#fafafa;line-height:1.6;">'
        + '※ 본 산출표는 도면 형상을 기반으로 자동 분해 및 추출한 물량산출(MTO) 명세입니다.<br/>'
        + '※ 원자재 단가: ' + (item.costBreakdown?.materialLabel || 'MDF/PB') + ' 단위기준가 적용 (출처: 한국물가정보지 2026년도)'
        + '</div></div>';
    })()}
    
    <table style="margin-bottom: 8px;">
      <thead>
        <tr>
          <th style="width:15%">항목</th>
          <th style="width:20%">산출명칭</th>
          <th style="width:15%">비율/단가</th>
          <th style="width:25%">금액(원)</th>
          <th style="width:25%">비고</th>
        </tr>
      </thead>
      <tbody>
        ${item.costBreakdown ? `
        <!-- 자재비 -->
        <tr>
          <td rowspan="3" style="font-weight:700; background:#f7fafc;">자재비</td>
          <td style="text-align:left; padding-left:8px;">원자재 (${item.costBreakdown.materialLabel})</td>
          <td class="num">-</td>
          <td class="price">${fmt(item.costBreakdown.rawMaterialCost)}</td>
          <td class="cat" style="font-size:7px;">${item.costBreakdown.materialLabel} ${fmt(item.costBreakdown.unitPricePerM2 ?? 0)}원/㎡ × ${item.costBreakdown.totalMaterialAreaM2 ? (item.costBreakdown.totalMaterialAreaM2 * (item.costBreakdown.lossMultiplier ?? 1)).toFixed(3) : item.surfaceM2.toFixed(3)}㎡ (재단로스 ${Math.round(((item.costBreakdown.lossMultiplier ?? 1.18) - 1) * 100)}% 소요량 반영)<br/><span style="color:#888;">출처: 한국물가정보 2026.01 p.${item.costBreakdown.materialSourcePage ?? '328'}</span></td>
        </tr>
        <tr>
          <td style="text-align:left; padding-left:8px;">CNC 재단비</td>
          <td class="num">-</td>
          <td class="price">${fmt(item.costBreakdown.cuttingCost)}</td>
          <td class="cat"></td>
        </tr>
        <tr>
          <td style="text-align:left; padding-left:8px;">엣지 마감</td>
          <td class="num">-</td>
          <td class="price">${fmt(item.costBreakdown.edgeCost)}</td>
          <td class="cat"></td>
        </tr>
        <tr class="subtotal-row">
          <td colspan="3" style="text-align:right; padding-right:12px;">자재비 소계</td>
          <td class="total-price" style="color:#2c5282;">${fmt(item.costBreakdown.materialTotal)}</td>
          <td></td>
        </tr>
        
        <!-- 고정제작비 / 인건비 -->
        <tr>
          <td rowspan="${item.costBreakdown.optionsCost > 0 ? 5 : 4}" style="font-weight:700; background:#f8fafc;">노무/외주가공</td>
          <td style="text-align:left; padding-left:8px;">조립 인건비 (내장가구공)</td>
          <td class="num">${item.costBreakdown.assemblyLaborDays ? `${item.costBreakdown.assemblyLaborDays}인 × ${fmt(item.costBreakdown.assemblyLaborRate || 252641)}` : '-'}</td>
          <td class="price">${fmt(item.costBreakdown.assemblyCost)}</td>
          <td class="cat">품셈 [1011]</td>
        </tr>
        <tr>
          <td style="text-align:left; padding-left:8px;">도장 마감비 (도장공)</td>
          <td class="num">${item.costBreakdown.paintingLaborDays ? `${item.costBreakdown.paintingLaborDays}인 × ${fmt(item.costBreakdown.paintingLaborRate || 282536)}` : '-'}</td>
          <td class="price">${fmt(item.costBreakdown.paintingCost)}</td>
          <td class="cat">${item.costBreakdown.paintingLaborDays ? '품셈 [1012]' : ''}</td>
        </tr>
        <tr>
          <td style="text-align:left; padding-left:8px;">인조대리석/유리/인조가죽쿠션</td>
          <td class="num">-</td>
          <td class="price">${fmt(item.costBreakdown.outsourcingCost)}</td>
          <td class="cat"></td>
        </tr>
        ${item.costBreakdown.optionsCost > 0 ? `
        <tr>
          <td style="text-align:left; padding-left:8px; color:#b7791f; font-weight:600;">하드웨어/콘센트 옵션</td>
          <td class="num">-</td>
          <td class="price" style="color:#b7791f;">${fmt(item.costBreakdown.optionsCost)}</td>
          <td class="cat">현장 옵션 반영</td>
        </tr>
        ` : ''}
        <tr>
          <td style="text-align:left; padding-left:8px;">보양 및 포장</td>
          <td class="num">-</td>
          <td class="price">${fmt(item.costBreakdown.packagingCost)}</td>
          <td class="cat"></td>
        </tr>
        <tr class="subtotal-row">
          <td colspan="3" style="text-align:right; padding-right:12px;">제작/옵션 소계</td>
          <td class="total-price" style="color:#2c5282;">${fmt(item.costBreakdown.manufacturingTotal)}</td>
          <td></td>
        </tr>

        <!-- 물류/설치비 -->
        <tr>
          <td rowspan="3" style="font-weight:700; background:#f8fafc;">운송/설치</td>
          <td style="text-align:left; padding-left:8px;">차량 운송비 (화물차운전)</td>
          <td class="num">${item.w ? `${Math.max(0.02, Math.round((item.w/1000 * item.d/1000 * item.h/1000 / 10) * 100) / 100)}인 × 238,936` : '-'}</td>
          <td class="price">${fmt(item.costBreakdown.transportCost)}</td>
          <td class="cat">품셈 [1049]</td>
        </tr>
        <tr>
          <td style="text-align:left; padding-left:8px;">현장 상하차/양중</td>
          <td class="num">-</td>
          <td class="price">${fmt(item.costBreakdown.loadingCost)}</td>
          <td class="cat"></td>
        </tr>
        <tr>
          <td style="text-align:left; padding-left:8px;">현장 세팅 (보통인부)</td>
          <td class="num">${item.costBreakdown.settingLaborDays ? `${item.costBreakdown.settingLaborDays}인 × ${fmt(item.costBreakdown.settingLaborRate || 172068)}` : '-'}</td>
          <td class="price">${fmt(item.costBreakdown.settingLaborCost)}</td>
          <td class="cat">${item.costBreakdown.settingLaborDays ? '품셈 [1002]' : ''}</td>
        </tr>
        <tr class="subtotal-row">
          <td colspan="3" style="text-align:right; padding-right:12px;">설치비 소계</td>
          <td class="total-price" style="color:#2c5282;">${fmt(item.costBreakdown.installTotal)}</td>
          <td></td>
        </tr>
        
        <!-- 기타/이윤 -->
        <tr>
          <td rowspan="3" style="font-weight:700; background:#f7fafc;">제경비/이윤</td>
          <td style="text-align:left; padding-left:8px;">일반관리비</td>
          <td class="num">${item.costBreakdown.generalMgmtCost === 0 ? '0%' : `약 ${Math.round((item.costBreakdown.appliedRates?.generalMgmt || 0.08)*100)}%`}</td>
          <td class="price">${fmt(item.costBreakdown.generalMgmtCost)}</td>
          <td class="cat"></td>
        </tr>
        <tr>
          <td style="text-align:left; padding-left:8px;">공과잡비</td>
          <td class="num">${item.costBreakdown.miscExpenseCost === 0 ? '0%' : `약 ${Math.round((item.costBreakdown.appliedRates?.miscExpense || 0.05)*100)}%`}</td>
          <td class="price">${fmt(item.costBreakdown.miscExpenseCost)}</td>
          <td class="cat"></td>
        </tr>
        <tr>
          <td style="text-align:left; padding-left:8px;">기업 이윤</td>
          <td class="num">${item.costBreakdown.profitCost === 0 ? '0%' : `약 ${Math.round((item.costBreakdown.appliedRates?.profit || 0.15)*100)}%`}</td>
          <td class="price">${fmt(item.costBreakdown.profitCost)}</td>
          <td class="cat"></td>
        </tr>
        
        <tr class="subtotal-row">
          <td colspan="3" style="text-align:right; padding-right:12px; border:none; background:transparent;">단일품목 1개 기본단가</td>
          <td class="total-price" style="border:none; background:transparent; font-size:12px;">${fmt(item.unitPrice)}</td>
          <td style="border:none; background:transparent; font-size:8px;">* VAT 별도 대상</td>
        </tr>
        <tr class="subtotal-row">
          <td colspan="3" style="text-align:right; padding-right:12px; border:none; background:transparent;">총 견적금액 (수량 ${item.qty}개)</td>
          <td class="total-price" style="font-size:14px; border:none; background:transparent;">${fmt(item.totalPrice)}</td>
          <td style="border:none; background:transparent;"></td>
        </tr>
        ` : `
        <tr><td colspan="5">옵션 및 상세 원가 내역을 불러올 수 없습니다.</td></tr>
        `}
      </tbody>
    </table>

    ${item.marketComparison ? `
    <div style="margin-top: 24px; border-top: 2px solid #111; border-bottom: 2px solid #111; page-break-inside: avoid;">
      <div style="background: transparent; padding: 12px 6px; font-size: 11px; font-weight: 800; color: #111; border-bottom: 1px solid #111; display: flex; align-items: center; gap: 6px;">
        유사물품 시중가 및 나라장터(G2B) 비교 단가참조표
      </div>
      <table style="margin: 0; border: none;">
        <thead>
          <tr style="background: transparent;">
            <th style="border: none; border-bottom: 1px solid #eaeaea; color: #111;">SnapGRID 최종단가</th>
            <th style="border: none; border-bottom: 1px solid #eaeaea; color: #111;">G2B 나라장터 등록가</th>
            <th style="border: none; border-bottom: 1px solid #eaeaea; color: #666;">퍼시스(Fursys) 비교</th>
            <th style="border: none; border-bottom: 1px solid #eaeaea; color: #666;">리바트(Livart) 비교</th>
            <th style="border: none; border-bottom: 1px solid #eaeaea; color: #666;">코아즈(Koas) 비교</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td style="font-weight: 800; font-size: 14px; color: #111; border: none; border-right: 1px dashed #e5e5e5;">₩ ${fmt(item.unitPrice)}</td>
            <td style="font-weight: 800; font-size: 13px; color: #111; border: none; border-right: 1px dashed #e5e5e5;">
              ₩ ${fmt(item.marketComparison.naraPrice)}
              <div style="font-size: 9px; margin-top: 4px; font-weight: 600;"><a href="${item.marketComparison.naraUrl}" target="_blank" rel="noopener noreferrer" style="color: #666; text-decoration: underline;">규격매칭 검색결과</a></div>
            </td>
            <td style="font-size: 11px; border: none; border-right: 1px dashed #e5e5e5;">₩ ${fmt(item.marketComparison.fursysPrice)}</td>
            <td style="font-size: 11px; border: none; border-right: 1px dashed #e5e5e5;">₩ ${fmt(item.marketComparison.livartPrice)}</td>
            <td style="font-size: 11px; border: none;">₩ ${fmt(item.marketComparison.koaPrice)}</td>
          </tr>
        </tbody>
      </table>
    </div>
    ` : ''}

    <!-- 제조 분해 및 마감산출서 (3D 겨냥도 + 파츠 비례) -->
    ${(() => {
      const calcParts = (item as any).calcParts || [];
      if (calcParts.length === 0) return '';
      const openRatio = (item as any).openRatio || 0;
      const plan = decomposeForManufacturing(calcParts, item.w, item.d, item.h, openRatio, (item as any).moduleBreakdown);

      // ★ renderIsometric v2 — SSoT (파츠 리스트) 기반 아이소 SVG 합성
      function renderIsometricSSoT(
        modName: string, mW: number, mD: number, mH: number, count: number, openRatio: number
      ) {
        const parts = calcParts.map((p: any) => ({
          partName: p.partName,
          w: p.w,
          d: p.d,
          h: p.h,
          qty: parseInt(p.calculation) || 1
        }));

        const spec: any = {
          code: item.code || '',
          name: modName,
          W: mW,
          D: mD,
          H: mH,
          bayCount: count,
          parts: parts,
          openPanels: openRatio > 0.05 ? [{ ratio: openRatio, side: 'front' }] : []
        };
        
        return renderIsometricSvg(spec);
      }

      // 개요 블록
      var html = '<div style="margin-top:24px;border:1px solid #94a3b8;border-top:3px solid #1e293b;page-break-inside:avoid;">'
        + '<div style="background:#1e293b;color:#fff;padding:10px 14px;font-size:10px;font-weight:800;letter-spacing:0.5px;">'
        + '▣ 제조 분해 및 구조 마감산출서 — 원판 1220×2440 (4×8) 기준</div>'
        + '<div style="padding:10px 14px;background:#f1f5f9;font-size:8.5px;line-height:1.6;color:#334155;">';
      plan.assemblyOverview.forEach(function(line) { html += '<div>' + line + '</div>'; });
      html += '</div>';

      // 모듈 중복 클러스터링 (19개 Bay가 동일하면 "1 Bay x 19" 로 압축 표현)
      var clusteredModules: { mod: any, count: number, firstIndex: number, lastIndex: number }[] = [];
      plan.modules.forEach(function(mod, idx) {
        if (clusteredModules.length > 0) {
           var last = clusteredModules[clusteredModules.length - 1];
           // 규격과 이름 뒷부분(박스 종류)이 완전히 같으면 하나로 묶음
           var nameSuffix = mod.name.split('—')[1];
           var lastNameSuffix = last.mod.name.split('—')[1];
           if (last.mod.width === mod.width && last.mod.height === mod.height && last.mod.depth === mod.depth && nameSuffix === lastNameSuffix) {
              last.count++;
              last.lastIndex = idx + 1;
              return;
           }
        }
        var shortType = mod.name.split('—')[1] || '';
        clusteredModules.push({ mod: mod, count: 1, firstIndex: idx + 1, lastIndex: idx + 1 });
      });

      // 클러스터 렌더링
      clusteredModules.forEach(function(cluster) {
        var mod = cluster.mod;
        var count = cluster.count;
        var isStackedBox = mod.name.indexOf('상하') >= 0;
        var lowerH = mod.height, upperH = 0;
        var hasLowerBack = openRatio < 0.5;
        var hasUpperBack = openRatio < 0.8;
        
        if (isStackedBox) {
          mod.factorySteps.forEach(function(s: string) {
            var lm = s.match(/하부 BOX.*?×(\d+)\)/);
            if (lm) lowerH = parseInt(lm[1]);
            var um = s.match(/상부 BOX.*?×(\d+)\)/);
            if (um) upperH = parseInt(um[1]);
          });
          if (upperH === 0) { lowerH = Math.round(mod.height * 0.68); upperH = mod.height - lowerH; }
        }
        
        var bayLabel = count > 1 ? 'Bay ' + cluster.firstIndex + '~' + cluster.lastIndex + ' (총 ' + count + '세트)' : 'Bay ' + cluster.firstIndex;
        var fullTitle = '📦 ' + bayLabel + ' ' + (mod.name.indexOf('—')>=0 ? mod.name.substring(mod.name.indexOf('—')) : '');

        html += '<div style="border-top:2px solid #475569;padding:16px 14px;page-break-inside:avoid;">'
          + '<div style="font-size:11px;font-weight:900;color:#0f172a;margin-bottom:10px;border-bottom:1px solid #e2e8f0;padding-bottom:8px; display:flex; justify-content:space-between; align-items:center;">'
          + '<div>' + fullTitle + ' <span style="font-size:9.5px; color:#64748b; font-weight:600; margin-left:6px;">('+mod.width+'×'+mod.depth+'×'+mod.height+'mm)</span></div>'
          + (mod.fitsElevator ? '<span style="color:#059669;font-size:9px;background:#d1fae5;padding:3px 8px;border-radius:12px;">✅ EV양중 100% OK</span>' : '<span style="color:#dc2626;font-size:9px;background:#fee2e2;padding:3px 8px;border-radius:12px;">⚠ EV양중 초과 (분해/뉘어서 운반)</span>')
          + '</div>';

        // 3D 겨냥도 섹션 (클러스터 단위)
        html += '<div style="display:flex;gap:24px;margin-bottom:16px;align-items:flex-end;justify-content:center;background:#f8fafc;padding:16px 0;border-radius:8px;border:1px solid #e2e8f0;">';

        html += '<div style="text-align:center;flex-shrink:0;">'
          + '<div style="font-size:9px;font-weight:800;color:#0f172a;margin-bottom:4px;letter-spacing:0.5px;">[가구 모듈 계획 - 파츠 SSoT 검증]</div>'
          + renderIsometricSSoT(mod.name, mod.width, mod.depth, mod.height, count, openRatio)
          + '</div>';

        html += '</div>';

        // 패널 상세 테이블 (총량 표시 추가)
        html += '<div style="flex:1;overflow:hidden;margin-bottom:12px;">'
          + '<div style="font-size:8.5px;font-weight:800;color:#334155;margin-bottom:6px;">📌 1모듈 기준 파츠 리스트 (현장 도달 시 규격)</div>'
          + '<table style="width:100%;font-size:7.5px;border-collapse:collapse;border:1px solid #cbd5e1;">'
          + '<tr style="background:#1e293b;color:#fff;font-weight:700;">'
          + '<td style="padding:4px 6px;">공정</td>'
          + '<td style="padding:4px 6px;">파츠명</td>'
          + '<td style="padding:4px 6px;text-align:center;">가로(W)</td>'
          + '<td style="padding:4px 6px;text-align:center;">세로(H)</td>'
          + '<td style="padding:4px 6px;text-align:center;">두께(T)</td>'
          + '<td style="padding:4px 6px;text-align:center;">모듈당 수량</td>'
          + '<td style="padding:4px 6px;text-align:center;background:#0f172a;">총 필요량</td>'
          + '<td style="padding:4px 6px;text-align:center;">원판 소요</td>'
          + '<td style="padding:4px 6px;">안전 상태</td></tr>';

        mod.panels.forEach(function(p: any, pi: number) {
          var rowBg = pi % 2 === 0 ? '#fff' : '#f8fafc';
          var phaseTag = p.phase === 'factory' ? '<span style="color:#2563eb;font-weight:800;">🏭공장</span>' : '<span style="color:#d97706;font-weight:800;">🔧현장</span>';
          var fitsSheet = p.splitNote === '단일 원판 재단';
          var totalQty = p.qty * count;
          var totalSheets = p.sheetsNeeded * count;
          
          html += '<tr style="background:' + rowBg + ';">'
            + '<td style="padding:4px 6px;border-bottom:1px solid #e2e8f0;">' + phaseTag + '</td>'
            + '<td style="padding:4px 6px;border-bottom:1px solid #e2e8f0;font-weight:700;color:#1e293b;">' + p.partName + '</td>'
            + '<td style="padding:4px 6px;border-bottom:1px solid #e2e8f0;text-align:center;">' + p.cutW + '</td>'
            + '<td style="padding:4px 6px;border-bottom:1px solid #e2e8f0;text-align:center;">' + p.cutH + '</td>'
            + '<td style="padding:4px 6px;border-bottom:1px solid #e2e8f0;text-align:center;">' + p.thickness + '</td>'
            + '<td style="padding:4px 6px;border-bottom:1px solid #e2e8f0;text-align:center;color:#64748b;">x ' + p.qty + '</td>'
            + '<td style="padding:4px 6px;border-bottom:1px solid #e2e8f0;text-align:center;font-weight:800;color:#0f172a;background:#f1f5f9;">' + totalQty + '개</td>'
            + '<td style="padding:4px 6px;border-bottom:1px solid #e2e8f0;text-align:center;color:#475569;">' + totalSheets + '장</td>'
            + '<td style="padding:4px 6px;border-bottom:1px solid #e2e8f0;font-weight:600;color:' + (fitsSheet ? '#059669' : '#dc2626') + ';">'
            + (fitsSheet ? '✓ ' + p.splitNote : '⚠ ' + p.splitNote) + '</td></tr>';
        });
        html += '</table></div></div>';

        // 조립순서 (타임라인)
        html += '<div style="display:flex;gap:16px;font-size:7.5px;padding:8px 0;border-top:1px dotted #94a3b8;">'
          + '<div style="flex:1;"><div style="font-weight:800;color:#1e3a5f;margin-bottom:3px;">🏭 공장 조립 순서</div>';
        mod.factorySteps.forEach(function(s: string) {
          var isHeader = s.indexOf('──') >= 0;
          html += '<div style="color:' + (isHeader ? '#0f172a;font-weight:700;margin-top:4px' : '#334155') + ';">' + s + '</div>';
        });
        html += '</div><div style="flex:1;"><div style="font-weight:800;color:#7c2d12;margin-bottom:3px;">🔧 현장 설치 순서</div>';
        mod.siteSteps.forEach(function(s: string) { html += '<div style="color:#334155;">' + s + '</div>'; });
        html += '</div></div>';
        
        // 패킹단위 요약 보고서 (지시사항 반영)
        var modUnitCost = item.unitPrice / plan.modules.length;
        html += '<div style="margin-top:8px; background:#fefce8; border:1px solid #fef08a; padding:8px 12px; font-size:8px; border-radius:4px;">'
          + '<div style="font-weight:800; color:#854d0e; margin-bottom:4px; font-size:8.5px;">📦 패킹 배송 단위 및 단가 요약 (Packing & Shipping)</div>'
          + '<div style="display:flex; justify-content:space-between; align-items:center; line-height:1.4;">'
          + '<span style="color:#713f12;">• 분해 패킹 덩어리 수: <b style="font-size:8.5px;">' + (isStackedBox ? '2개 (상하로 나뉨)' : '1개 (원박스)') + '</b> × ' + count + '세트 = <b>총 ' + (count * (isStackedBox ? 2 : 1)) + ' 패킹</b></span>'
          + '<span style="color:#713f12;">• 패킹별 최대 부피 규격: <b style="font-size:8.5px;">W'+mod.width+' × D'+mod.depth+' × H'+(isStackedBox ? Math.max(lowerH, upperH) : mod.height)+'</b> mm</span>'
          + '<span style="color:#713f12;">• 단위 모듈(세트)당 단가 배분: <b style="font-size:9.5px; color:#b45309;">₩' + fmt(modUnitCost) + '</b>/1모듈</span>'
          + '</div>';
          
        html += '<div style="margin-top:6px; padding-top:6px; border-top:1px dashed #fcd34d; display:flex; gap:12px; font-size:7.5px; color:#15803d; font-weight:600;">'
          + '<span>✅ 정합성 검증: ' + count + '모듈 × ₩' + fmt(modUnitCost) + ' = 총 단가 ₩' + fmt(modUnitCost * count) + ' (산출 오류 없음, 원판 로스 및 마감재 정상 수렴 반영완료)</span>'
          + '</div></div></div>';
      });

      html += '<div style="padding:8px 14px;font-size:7.5px;color:#64748b;border-top:1px solid #475569;background:#f8fafc;">'
        + '※ 원판 1220×2440mm (4×8) | 네스팅82% (로스' + plan.wastePercent + '%) | 총 원판 ' + plan.totalSheets + '장 (' + plan.totalSheetAreaM2 + '㎡) | '
        + (openRatio > 0 ? 'OPEN ' + Math.round(openRatio * 100) + '% 뒷판 제외 반영' : '전면 폐합 구조')
        + '</div></div>';

      return html;
    })()}

  </div>
  `).join('') : ''}

  <!-- ── 부록: 노무비 산출근거 ── -->
  <div class="page" style="display: flex; flex-direction: column;">
    <div class="header">
      <div class="header-left">
        <div class="sub">Appendix — Labor Rates</div>
        <h1>노무비 산출근거표</h1>
      </div>
      <div class="header-right">
        디자인인사이트 SnapGRID
      </div>
    </div>
    
    <div style="flex:1;">
      <p style="font-size: 11px; color: #475569; margin-bottom: 24px; line-height: 1.6;">
        본 단가표는 <strong>대한건설협회 공표 시중노임단가(2026.1.1 기준)</strong>를 적용하여 작성되었습니다.<br/>
        행정안전부 예규 "지방자치단체 입찰 및 계약 집행기준"에 따른 원가계산 제출 시 노무비 증빙 근거로 활용됩니다.
      </p>

      <table style="width: 100%; border-collapse: collapse; font-size: 10px; margin-bottom: 30px;">
        <thead>
          <tr style="background: #f8fafc; border-bottom: 2px solid #334155;">
            <th style="padding: 10px; text-align: left; font-weight: 700; color: #1e293b;">적용 직종명</th>
            <th style="padding: 10px; text-align: right; font-weight: 700; color: #1e293b;">시중 노임 단가 (원 / 인·일)</th>
          </tr>
        </thead>
        <tbody>
          ${Object.entries(costTaxonomy.labor_rates_krw).map(([job, rate]) => `<tr style="border-bottom: 1px solid #e2e8f0;">
              <td style="padding: 10px; color: #334155; font-weight: 600;">${job}</td>
              <td style="padding: 10px; text-align: right; color: #0f172a; font-family: monospace; font-size: 11px;">${fmt(rate as number)}</td>
            </tr>`).join('')}
        </tbody>
      </table>

      <div style="padding: 14px; background: #f1f5f9; border-left: 3px solid #64748b; font-size: 9px; color: #475569; line-height: 1.5;">
        <strong>[참고사항]</strong><br/>
        * 조사현장이 5개 미만인 직종이나 미조사 직종의 경우 관련 규정(건설업 임금실태조사 참고사항)에 의거하여 가장 인접한 직종의 단가를 반영하거나 이전 공표 단가를 상향 보정하여 적용하였습니다.<br/>
        * 노임단가는 1일 8시간 근무 기준이며, 현장 기본 양중 및 조립 세팅 비용을 구성하는 기준 단가입니다.
      </div>
    </div>
    
  </div>

  <!-- 도면 부록 -->
  ${includeDetails ? `
  <div class="page" style="page-break-before: always; margin-top: 12mm;">
    <div class="header" style="border-bottom: 3px solid #111; margin-bottom: 16px; padding-bottom: 12px;">
      <div class="header-left">
        <h2 style="font-size: 16px; color: #111; font-weight: 800;">[부록] 개별 품목 제작도면 및 도면 발췌</h2>
        <div class="sub" style="font-weight: 600; font-size: 11px; margin-top: 4px;">가구도면 이미지 목록</div>
      </div>
    </div>
    <div style="display: flex; flex-direction: column; gap: 24px;">
      ${estimate.items.map((item) => {
        const parts = (item as any).calcParts ? (item as any).calcParts.map((p: any) => ({
          partName: p.partName, w: p.w, d: p.d, h: p.h, qty: parseInt(p.calculation) || 1
        })) : [];
        const spec: any = {
          code: item.code || '', name: item.name,
          W: item.w, D: item.d, H: item.h,
          bayCount: 1, parts: parts,
          openPanels: ((item as any).openRatio || 0) > 0.05 ? [{ ratio: (item as any).openRatio, side: 'front' }] : []
        };
        const isoDataUrl = renderIsometricDataUrl(spec);

        return `
        <div style="page-break-inside: avoid; border: 1px solid #cbd5e1; border-radius: 8px; padding: 16px; background: #fafafa;">
          <h4 style="margin-bottom: 12px; font-size: 13px; color: #334155;">${item.code} (${item.name})</h4>
          <div style="width: 100%; height: 320px; background: #fff; display: flex; align-items: center; justify-content: center; overflow: hidden; border: 1px solid #e2e8f0;">
             <img src="${getSync(item.code) || (item as any).previewImage || isoDataUrl}" alt="${item.code} 도면 (${getSync(item.code) ? '원본 첨부' : 'AI 자동 생성'})" style="max-width: 100%; max-height: 100%; object-fit: contain;" />
          </div>
        </div>
      `;
      }).join('')}
    </div>
  </div>
  ` : ''}

<script>
  // Auto-focus for print on load
  window.onload = function() {
    document.title = '${estimate.projectName} — 제작가구 전문견적서';
  };
</script>
</body>
</html>`;

  const LC = {
    pkg: '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="vertical-align:text-bottom;margin-right:4px;display:inline-block;"><path d="M16.5 9.4 7.5 4.21"/><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/><polyline points="3.27 6.96 12 12.01 20.73 6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/></svg>',
    check: '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" style="vertical-align:text-bottom;margin-right:4px;display:inline-block;"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>',
    alert: '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" style="vertical-align:text-bottom;margin-right:4px;display:inline-block;"><path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>',
    fac: '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="vertical-align:text-bottom;margin-right:2px;display:inline-block;"><path d="M2 20a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V8l-7 5V8l-7 5V4a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2Z"/><path d="M17 18h1"/><path d="M12 18h1"/><path d="M7 18h1"/></svg>',
    wrench: '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="vertical-align:text-bottom;margin-right:2px;display:inline-block;"><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/></svg>',
    pin: '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="vertical-align:text-bottom;margin-right:4px;display:inline-block;"><line x1="12" y1="17" x2="12" y2="22"/><path d="M5 17h14v-1.76a2 2 0 0 0-1.11-1.79l-1.78-.9A2 2 0 0 1 15 10.68V6a3 3 0 0 0-3-3v0a3 3 0 0 0-3 3v4.68a2 2 0 0 1-1.11 1.87l-1.78.9A2 2 0 0 0 5 15.24Z"/></svg>'
  };

  const finalHtml = html
    .replace(/📦/g, LC.pkg)
    .replace(/✅/g, LC.check)
    .replace(/⚠/g, LC.alert)
    .replace(/🏭/g, LC.fac)
    .replace(/🔧/g, LC.wrench)
    .replace(/📌/g, LC.pin);

  // 새 창(또는 탭)을 열고 직접 HTML을 써넣어 렌더링
  const win = window.open('', '_blank');
  if (win) {
    win.document.open();
    win.document.write(finalHtml);
    win.document.close();
  } else {
    alert("팝업이 차단되었습니다. 팝업 차단을 해제해주세요.");
  }
}
